// CodebaseGuidePanel.js — structured Codebase Guide (Repo Playbook) entries.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "./api";
import { useAuth } from "./AuthContext";
import { canMutateRepos, VIEWER_READ_ONLY_MESSAGE } from "./orgPermissions";
import {
  CODEBASE_GUIDE_CATEGORIES,
  CODEBASE_GUIDE_PRIORITIES,
  guideCategoryLabel,
  guidePriorityLabel,
} from "./codebaseGuideConstants";
import "./CodebaseGuidePanel.css";

const EMPTY_FORM = {
  category: "architecture",
  title: "",
  body: "",
  pathScope: "",
  taskTypeScope: "",
  priority: "medium",
  visibilityPm: true,
  visibilityEngineer: true,
  isActive: true,
};

function splitScope(text) {
  return String(text || "")
    .split(/[\n,]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function entryToForm(entry) {
  return {
    category: entry.category || "architecture",
    title: entry.title || "",
    body: entry.body || "",
    pathScope: (entry.pathScope || []).join("\n"),
    taskTypeScope: (entry.taskTypeScope || []).join("\n"),
    priority: entry.priority || "medium",
    visibilityPm: entry.visibility?.pm !== false,
    visibilityEngineer: entry.visibility?.engineer !== false,
    isActive: entry.isActive !== false,
  };
}

function formToPayload(form) {
  return {
    category: form.category,
    title: form.title.trim(),
    body: form.body.trim(),
    pathScope: splitScope(form.pathScope),
    taskTypeScope: splitScope(form.taskTypeScope),
    priority: form.priority,
    visibility: {
      pm: Boolean(form.visibilityPm),
      engineer: Boolean(form.visibilityEngineer),
    },
    isActive: Boolean(form.isActive),
  };
}

function groupByCategory(entries) {
  const order = CODEBASE_GUIDE_CATEGORIES.map((c) => c.value);
  const groups = new Map(order.map((value) => [value, []]));
  for (const entry of entries) {
    const key = groups.has(entry.category) ? entry.category : "other";
    groups.get(key).push(entry);
  }
  return order
    .map((category) => ({ category, entries: groups.get(category) || [] }))
    .filter((g) => g.entries.length > 0);
}

function CodebaseGuidePanel({ repoId }) {
  const { user } = useAuth();
  const mutateAllowed = canMutateRepos(user);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const load = useCallback(async () => {
    if (!repoId) return;
    try {
      setLoading(true);
      setError("");
      const response = await api.listCodebaseGuide(repoId);
      setEntries(response.data.entries || []);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load Codebase Guide");
    } finally {
      setLoading(false);
    }
  }, [repoId]);

  useEffect(() => {
    load();
  }, [load]);

  const grouped = useMemo(() => groupByCategory(entries), [entries]);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormOpen(true);
    setError("");
  };

  const openEdit = (entry) => {
    setEditingId(entry._id);
    setForm(entryToForm(entry));
    setFormOpen(true);
    setError("");
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const handleSave = async () => {
    if (!repoId || !mutateAllowed) return;
    const payload = formToPayload(form);
    if (!payload.title || !payload.body) {
      setError("Title and body are required.");
      return;
    }
    try {
      setSaving(true);
      setError("");
      if (editingId) {
        await api.updateCodebaseGuideEntry(repoId, editingId, payload);
      } else {
        await api.createCodebaseGuideEntry(repoId, payload);
      }
      closeForm();
      await load();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to save guide entry");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (entry) => {
    if (!repoId || !mutateAllowed) return;
    const ok = window.confirm(`Delete “${entry.title}”? This cannot be undone.`);
    if (!ok) return;
    try {
      setError("");
      await api.deleteCodebaseGuideEntry(repoId, entry._id);
      if (editingId === entry._id) closeForm();
      await load();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to delete guide entry");
    }
  };

  if (!repoId) return null;

  return (
    <section className="codebase-guide-panel" data-testid="codebase-guide-panel">
      <div className="codebase-guide-panel-header">
        <h3>Operating rules</h3>
        <p>
          Structured guidance for how this codebase is worked — architecture constraints,
          test commands, do-not-touch areas, and review checklists.
        </p>
      </div>

      <div className="codebase-guide-toolbar">
        {mutateAllowed ? (
          <button type="button" className="primary-button" onClick={openCreate}>
            Add guide entry
          </button>
        ) : (
          <p className="codebase-guide-muted">{VIEWER_READ_ONLY_MESSAGE}</p>
        )}
        {loading && <p className="codebase-guide-muted">Loading…</p>}
      </div>

      {error && (
        <p className="codebase-guide-error" role="alert">
          {error}
        </p>
      )}

      {formOpen && mutateAllowed && (
        <div className="codebase-guide-form" data-testid="codebase-guide-form">
          <h4>{editingId ? "Edit guide entry" : "New guide entry"}</h4>
          <div className="codebase-guide-form-grid">
            <div className="codebase-guide-field">
              <label htmlFor="guide-category">Category</label>
              <select
                id="guide-category"
                value={form.category}
                onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
              >
                {CODEBASE_GUIDE_CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="codebase-guide-field">
              <label htmlFor="guide-priority">Priority</label>
              <select
                id="guide-priority"
                value={form.priority}
                onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))}
              >
                {CODEBASE_GUIDE_PRIORITIES.map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="codebase-guide-field full">
              <label htmlFor="guide-title">Title</label>
              <input
                id="guide-title"
                data-testid="codebase-guide-title"
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                maxLength={200}
                placeholder="e.g. Do not touch Stripe webhooks"
              />
            </div>
            <div className="codebase-guide-field full">
              <label htmlFor="guide-body">Guidance</label>
              <textarea
                id="guide-body"
                data-testid="codebase-guide-body"
                value={form.body}
                onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
                maxLength={8000}
                placeholder="What should a developer know or do?"
              />
            </div>
            <div className="codebase-guide-field">
              <label htmlFor="guide-paths">Path scope (one per line)</label>
              <textarea
                id="guide-paths"
                data-testid="codebase-guide-paths"
                value={form.pathScope}
                onChange={(e) => setForm((f) => ({ ...f, pathScope: e.target.value }))}
                placeholder="repo-map/server/src/webhooks/"
              />
            </div>
            <div className="codebase-guide-field">
              <label htmlFor="guide-tasks">Task type scope (one per line)</label>
              <textarea
                id="guide-tasks"
                value={form.taskTypeScope}
                onChange={(e) => setForm((f) => ({ ...f, taskTypeScope: e.target.value }))}
                placeholder="billing, auth, ui"
              />
            </div>
            <div className="codebase-guide-field full">
              <label>Visibility</label>
              <div className="codebase-guide-checkboxes">
                <label>
                  <input
                    type="checkbox"
                    checked={form.visibilityPm}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, visibilityPm: e.target.checked }))
                    }
                  />
                  PM
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={form.visibilityEngineer}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, visibilityEngineer: e.target.checked }))
                    }
                  />
                  Engineer
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={form.isActive}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, isActive: e.target.checked }))
                    }
                  />
                  Active
                </label>
              </div>
            </div>
          </div>
          <div className="codebase-guide-form-actions">
            <button
              type="button"
              className="primary-button"
              onClick={handleSave}
              disabled={saving}
              data-testid="codebase-guide-save"
            >
              {saving ? "Saving…" : editingId ? "Save changes" : "Create entry"}
            </button>
            <button type="button" onClick={closeForm} disabled={saving}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {!loading && entries.length === 0 && !formOpen && (
        <div className="codebase-guide-empty" data-testid="codebase-guide-empty">
          No operating rules yet. Add entries for architecture constraints, test commands,
          do-not-touch areas, and review checklists so Tasks and developer handoffs
          stay grounded in how this repo is actually worked.
        </div>
      )}

      {grouped.map(({ category, entries: categoryEntries }) => (
        <div key={category} className="codebase-guide-category" data-testid={`guide-category-${category}`}>
          <h4 className="codebase-guide-category-title">{guideCategoryLabel(category)}</h4>
          {categoryEntries.map((entry) => (
            <article
              key={entry._id}
              className={`codebase-guide-entry${entry.isActive ? "" : " is-inactive"}`}
              data-testid={`guide-entry-${entry._id}`}
            >
              <div className="codebase-guide-entry-top">
                <h5 className="codebase-guide-entry-title">{entry.title}</h5>
                {mutateAllowed && (
                  <div className="codebase-guide-entry-actions">
                    <button type="button" onClick={() => openEdit(entry)}>
                      Edit
                    </button>
                    <button type="button" onClick={() => handleDelete(entry)}>
                      Delete
                    </button>
                  </div>
                )}
              </div>
              <p className="codebase-guide-entry-body">{entry.body}</p>
              <div className="codebase-guide-badges">
                <span className={`codebase-guide-badge priority-${entry.priority}`}>
                  {guidePriorityLabel(entry.priority)}
                </span>
                {entry.visibility?.pm && (
                  <span className="codebase-guide-badge">PM</span>
                )}
                {entry.visibility?.engineer && (
                  <span className="codebase-guide-badge">Engineer</span>
                )}
                {!entry.isActive && (
                  <span className="codebase-guide-badge inactive">Inactive</span>
                )}
              </div>
              {(entry.pathScope?.length > 0 || entry.taskTypeScope?.length > 0) && (
                <p className="codebase-guide-scopes">
                  {entry.pathScope?.length > 0 && (
                    <>Paths: {entry.pathScope.join(", ")}</>
                  )}
                  {entry.pathScope?.length > 0 && entry.taskTypeScope?.length > 0 && " · "}
                  {entry.taskTypeScope?.length > 0 && (
                    <>Tasks: {entry.taskTypeScope.join(", ")}</>
                  )}
                </p>
              )}
            </article>
          ))}
        </div>
      ))}
    </section>
  );
}

export default CodebaseGuidePanel;
